import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  // Disable parallax/continuous motion for reduced-motion users and small/touch devices
  const reducedMotion = useReducedMotion();
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px), (hover: none), (pointer: coarse)');
    const update = () => setIsCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const motionEnabled = !reducedMotion && !isCompact;
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!motionEnabled || !containerRef.current) return;
    const {
      left,
      top,
      width,
      height
    } = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };
  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };
  const springConfig = {
    damping: 50,
    stiffness: 400,
    mass: 0.5
  };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);
  const layer1X = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);
  const layer1Y = useTransform(smoothY, [-0.5, 0.5], [-10, 10]);
  const layer2X = useTransform(smoothX, [-0.5, 0.5], [-20, 20]);
  const layer2Y = useTransform(smoothY, [-0.5, 0.5], [-20, 20]);
  const layer3X = useTransform(smoothX, [-0.5, 0.5], [-35, 35]);
  const layer3Y = useTransform(smoothY, [-0.5, 0.5], [-35, 35]);
  const tiltX = useTransform(smoothY, [-0.5, 0.5], [2, -2]);
  const tiltY = useTransform(smoothX, [-0.5, 0.5], [-2, 2]);
  return <section className="relative min-h-[100svh] flex items-center pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden" ref={containerRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} data-id="element-94">
      {/* Subtle Orange Glow */}
      <div className="absolute top-1/2 left-1/2 lg:left-1/4 -translate-x-1/2 lg:-translate-x-0 -translate-y-1/2 w-[150vw] max-w-[800px] h-[150vw] max-h-[800px] bg-glow-radial opacity-40 lg:opacity-50 pointer-events-none" data-id="element-95" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 w-full grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative z-10" data-id="element-96">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col gap-6 lg:gap-10 lg:pr-12" data-id="element-97">
          <div className="inline-flex items-center gap-2 px-3.5 md:px-4 py-1.5 rounded-full border border-white/10 bg-white/5 w-fit text-[13px] md:text-xs font-medium text-accent tracking-widest uppercase" data-id="element-98">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" data-id="element-99" />
            {t('hero.badge')}
          </div>

          <h1 className="text-[clamp(46px,12vw,58px)] leading-[0.96] tracking-[-0.04em] lg:text-[5.5rem] font-display font-medium lg:leading-[1.05] lg:tracking-tighter text-gradient max-w-[12ch] lg:max-w-none" data-id="element-100">
            {t('hero.title')}
          </h1>

          <p className="text-[18px] leading-[1.55] max-w-[34ch] md:text-xl text-white/60 lg:max-w-lg font-light" data-id="element-101">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 pt-2 lg:pt-4 w-full md:w-auto" data-id="element-102">
            <button className="w-full max-w-[320px] md:w-auto h-[56px] md:h-auto group bg-white text-background px-8 py-3.5 rounded-full text-[16px] md:text-sm font-medium hover:bg-white/90 md:hover:-translate-y-0.5 md:hover:shadow-[0_8px_20px_rgba(255,255,255,0.15)] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2" data-id="element-103">
              {t('hero.startExploring')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-104" />
            </button>
            <button className="w-full max-w-[320px] md:w-auto text-center px-8 py-3.5 rounded-full text-[15px] md:text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 md:hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300" data-id="element-105">
              {t('hero.seeHowItWorks')}
            </button>
          </div>
        </motion.div>

        {/* 3D Visualization Placeholder - Decision Graph */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative mt-8 lg:mt-0 aspect-square max-w-[380px] mx-auto lg:max-w-none lg:aspect-auto lg:h-[650px] w-full rounded-[2.5rem] glass-panel overflow-hidden border border-white/10 flex flex-col items-center justify-center group shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-id="element-106">
          <div className="absolute inset-0 bg-gradient-to-br from-surface/80 to-background/90 opacity-90" data-id="element-107" />
          <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] rounded-[2.5rem] pointer-events-none" data-id="element-108" />

          {/* Soft orange core glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[80px] pointer-events-none" data-id="element-109" />

          {/* Concentric guide rings */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] border border-white/5 rounded-full pointer-events-none" data-id="element-110" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] border border-white/5 rounded-full pointer-events-none" data-id="element-111" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-white/5 rounded-full pointer-events-none" data-id="element-112" />

          {/* Decision Graph Neural Structure */}
          <div className="relative z-10 w-full h-full flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity duration-700" style={{
          perspective: 1000
        }} data-id="element-113">
            <motion.svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" style={motionEnabled ? {
            x: layer1X,
            y: layer1Y,
            rotateX: tiltX,
            rotateY: tiltY
          } : undefined} animate={motionEnabled ? {
            rotate: [-1, 1, -1]
          } : undefined} transition={motionEnabled ? {
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          } : undefined} data-id="element-114">
              <defs data-id="element-115">
                <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-116">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.02)" data-id="element-117" />
                  <stop offset="50%" stopColor="rgba(255,106,0,0.5)" data-id="element-118" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0.02)" data-id="element-119" />
                </linearGradient>
                <radialGradient id="node-glow" cx="50%" cy="50%" r="50%" data-id="element-120">
                  <stop offset="0%" stopColor="rgba(255,106,0,0.8)" data-id="element-121" />
                  <stop offset="100%" stopColor="rgba(255,106,0,0)" data-id="element-122" />
                </radialGradient>
              </defs>

              {/* Connecting Lines */}
              <g stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" fill="none" data-id="element-123">
                <path d="M 200 200 L 100 100" data-id="element-124" />
                <path d="M 200 200 L 300 120" data-id="element-125" />
                <path d="M 200 200 L 120 280" data-id="element-126" />
                <path d="M 200 200 L 280 300" data-id="element-127" />
                <path d="M 100 100 L 150 50" data-id="element-128" />
                <path d="M 300 120 L 350 150" data-id="element-129" />
                <path d="M 120 280 L 80 250" data-id="element-130" />
                <path d="M 280 300 L 320 350" data-id="element-131" />
                <path d="M 100 100 L 120 280" data-id="element-132" />
                <path d="M 300 120 L 280 300" data-id="element-133" />
              </g>

              {/* Animated Signal Pulses */}
              <motion.path d="M 200 200 L 100 100" stroke="url(#line-grad)" strokeWidth="2" fill="none" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: [0, 1, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-134" />
              <motion.path d="M 200 200 L 300 120" stroke="url(#line-grad)" strokeWidth="2" fill="none" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: [0, 1, 0]
            }} transition={{
              duration: 2.5,
              delay: 1,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-135" />
              <motion.path d="M 200 200 L 120 280" stroke="url(#line-grad)" strokeWidth="2" fill="none" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: [0, 1, 0]
            }} transition={{
              duration: 3.5,
              delay: 0.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-136" />
              <motion.path d="M 200 200 L 280 300" stroke="url(#line-grad)" strokeWidth="2" fill="none" initial={{
              pathLength: 0,
              opacity: 0
            }} animate={{
              pathLength: 1,
              opacity: [0, 1, 0]
            }} transition={{
              duration: 2.8,
              delay: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-137" />

              {/* Nodes */}
              {/* Central Hub */}
              <circle cx="200" cy="200" r="16" fill="url(#node-glow)" className="animate-pulse" data-id="element-138" />
              <circle cx="200" cy="200" r="6" fill="#FF6A00" data-id="element-139" />
              <circle cx="200" cy="200" r="2" fill="#fff" data-id="element-140" />

              {/* Primary Nodes */}
              <circle cx="100" cy="100" r="5" fill="rgba(255,255,255,0.9)" data-id="element-141" />
              <circle cx="300" cy="120" r="5" fill="rgba(255,255,255,0.9)" data-id="element-142" />
              <circle cx="120" cy="280" r="5" fill="rgba(255,255,255,0.9)" data-id="element-143" />
              <circle cx="280" cy="300" r="5" fill="rgba(255,255,255,0.9)" data-id="element-144" />

              {/* Secondary Nodes (Blurred for Depth of Field) */}
              <g filter="blur(1.5px)" opacity="0.6" data-id="element-145">
                <circle cx="150" cy="50" r="3" fill="rgba(255,255,255,0.5)" data-id="element-146" />
                <circle cx="350" cy="150" r="3" fill="rgba(255,255,255,0.5)" data-id="element-147" />
                <circle cx="80" cy="250" r="3" fill="rgba(255,255,255,0.5)" data-id="element-148" />
                <circle cx="320" cy="350" r="3" fill="rgba(255,255,255,0.5)" data-id="element-149" />
              </g>
            </motion.svg>

            <div className="absolute bottom-10 flex flex-col items-center gap-2" data-id="element-150">
              <div className="px-4 py-1.5 rounded-full bg-background/60 backdrop-blur-md border border-white/10 text-xs font-mono text-white/60 tracking-widest uppercase shadow-[0_4px_12px_rgba(0,0,0,0.2)]" data-id="element-151">
                {t('hero.network')}
              </div>
            </div>
          </div>

          {/* Subtle grid background */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" data-id="element-152" />
        </motion.div>
      </div>
    </section>;
}